/**
 * Compact count label for the map cluster donuts: at most four characters
 * ("999", "1.2k", "28k", "384k", "1.2M"). Only the decimal separator follows the
 * locale — `Intl` compact notation is not used because e.g. German does not
 * abbreviate thousands ("28.000" would not fit the donut).
 */

const UNITS = [
  { value: 1e9, suffix: "B" },
  { value: 1e6, suffix: "M" },
  { value: 1e3, suffix: "k" },
];

const formatMantissa = (value: number, digits: number, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale || "en", {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
      useGrouping: false,
    }).format(value);
  } catch {
    return value.toFixed(digits);
  }
};

export const formatCompactCount = (count: number, locale = "en"): string => {
  const n = Math.max(0, Math.round(count));
  if (n < 1000) return String(n);

  for (let i = 0; i < UNITS.length; i++) {
    const unit = UNITS[i];
    if (n < unit.value) continue;

    const scaled = n / unit.value;
    const digits = scaled < 10 ? 1 : 0;
    const rounded = Number(scaled.toFixed(digits));

    // 999 950 rounds up to 1000k → show as the next unit instead.
    if (rounded >= 1000 && i > 0) {
      return `1${UNITS[i - 1].suffix}`;
    }
    return `${formatMantissa(rounded, rounded >= 10 ? 0 : digits, locale)}${unit.suffix}`;
  }

  return String(n);
};
